<script setup>
// Vue twin of <x-basket-item> (resources/views/components/basket-item.blade.php +
// App\View\Components\BasketItem). Same elements and class hooks, no styling of its
// own - components/basket-item.scss styles both implementations, which is what stops
// them drifting apart. See docs/blade-to-vue-components.md.
//
// Renders the <li> itself, exactly as the Blade component does, so callers put it in
// a <ul class="basket_items">.
//
// Props mirror the basket payload BasketController@index returns (type, name, date,
// people, price), so a payload item can be spread straight in with no reshaping - the
// contract the Blade class's own docblock records.
//
// Removal is a single `remove` emit with no payload. The consumer knows the guid; this
// component only knows it was asked to go. Blade's control is not wired up at all,
// so there is nothing to diverge from. `edit` works the same way, for the same reason.
//
// The edit control is withheld rather than disabled when a row cannot be changed, matching
// how <x-form-card> treats its own: a pencil that looks dead and does nothing is worse than
// no pencil. Two reasons a row cannot be changed, both decided server-side and arriving on
// the payload as `editable` - a gift item (out of scope) and an item holding a live Phorest
// reservation, which has to be cancelled at checkout before its slot can be re-picked. See
// docs/feature-requests/checkout-location-date-editing-scope-technical.md §11.
import { computed, watch } from 'vue';
import Chip from '@/components/Chip.vue';
import SvgIcon from '@/components/SvgIcon.vue';
import { formatPrice } from '@/shared/formatPrice.js';

const props = defineProps({
    type:   { type: String, required: true },
    name:   { type: String, required: true },
    price:  { type: Number, required: true },
    // The pre-discount figure, struck through before the price. Null on any line that is
    // not discounted, which is nearly all of them. Only the last-minute discount sets it
    // today - see BasketController::lineWasPrice().
    wasPrice: { type: Number, default: null },
    date:   { type: String, default: null },
    people: { type: Number, default: null },
    // Which spa the booking is at. Null for a monetary gift voucher, which is spendable
    // anywhere and so is bound to nothing. Shown as part of the row's meta line - before
    // this the sidebar never said which spa a line was for at all.
    locationName: { type: String, default: null },
    // Whether this row offers an edit control. Defaults to false so a caller that has not
    // been updated shows what it always did rather than a control it cannot service.
    editable: { type: Boolean, default: false },
    // Whether an edit is unavailable specifically because a reservation is being held, which
    // is the one case worth explaining - the way out is on the checkout page.
    locked: { type: Boolean, default: false },
    // Named where a voucher is paying for this line, in which case the row states that in
    // place of its price - see BasketController::voucherProvider().
    voucherProvider: { type: String, default: null },
});

const emit = defineEmits(['remove', 'edit']);

// Mirrors BasketItem::TYPES one-to-one rather than importing a shared map. The keys are
// the payload's own values - ProductType's cases plus 'gift', which is not a ProductType.
//
// BookingSummary.vue holds the same keys and the same icons under different label casing,
// and ProductCard.vue/TypeFilter.vue hold the same icons again under the search page's
// vocabulary ('spa-day', not 'package'). Kept local deliberately: the labels are
// per-context by design, and a twin that took its wording from anywhere other than its
// own Blade half could drift from it. See docs/frontend-components.md.
const TYPES = {
    'package':   { label: 'Spa day', icon: 'robe' },
    'spa-break': { label: 'Spa break', icon: 'moon' },
    'treatment': { label: 'Treatment', icon: 'flower' },
    'gift':      { label: 'Gift voucher', icon: 'gift' },
};

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// The same CLDR data App\View\Components\BasketItem formats with, via ICU on that side
// and Intl here, so the two halves cannot disagree about a month's abbreviation.
const DATE_FORMAT = new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
});

// Watched rather than checked once at setup, unlike Chip's call-site rules: the basket is
// refetched wholesale after every add and remove, and the consumer keys its rows on the
// guid, so Vue reuses this instance with fresh values rather than re-running setup.
watch(() => props.type, (type) => {
    if (! (type in TYPES)) {
        console.error(
            `[BasketItem] type must be one of [${Object.keys(TYPES).join(', ')}], got [${type}]. `
            + 'Rendering the line with no type chip.'
        );
    }
}, { immediate: true });

watch(() => props.date, (date) => {
    if (date !== null && ! DATE_PATTERN.test(date)) {
        console.error(
            `[BasketItem] date must be YYYY-MM-DD, got [${date}]. `
            + 'Rendering the line with no date.'
        );
    }
}, { immediate: true });

const typeLabel = computed(() => TYPES[props.type]?.label ?? null);
const icon = computed(() => TYPES[props.type]?.icon ?? null);

// Formatted here rather than upstream, matching the Blade class, so the shown date cannot
// drift from the stored one. Built from the date parts, not parsed from the ISO string: an
// ISO date string is treated as UTC, which lands on the previous day in any negative-offset
// timezone.
const dateLabel = computed(() => {
    if (props.date === null || ! DATE_PATTERN.test(props.date)) {
        return null;
    }

    const [year, month, day] = props.date.split('-').map(Number);

    return DATE_FORMAT.format(new Date(year, month - 1, day));
});

// A single guest is the norm, so saying so adds a line without adding information.
const peopleLabel = computed(() => (
    props.people !== null && props.people > 1 ? `For ${props.people}` : null
));

const hasMeta = computed(() => (
    dateLabel.value !== null || peopleLabel.value !== null || props.locationName !== null
));

// The visible control is an icon shared by every row, so without the item's name a screen
// reader announces several identical "remove" buttons. Same for edit.
const removeLabel = computed(() => `Remove ${props.name} from your basket`);
const editLabel = computed(() => `Change the spa, date or guests for ${props.name}`);

const priceString = computed(() => formatPrice(props.price));
const wasPriceString = computed(() => (props.wasPrice === null ? null : formatPrice(props.wasPrice)));
</script>

<template>
    <li class="basket_item">
        <div class="basket_item_header">
            <p class="basket_item_name">{{ name }}</p>
            <!-- Edit sits to the left of remove, so the destructive action stays in the
                 corner where it has always been and nothing moves under a returning
                 customer's finger. Withheld, not disabled - see the note at the top. -->
            <button
                v-if="editable"
                type="button"
                class="basket_item_edit plain"
                :aria-label="editLabel"
                @click="emit('edit')"
            >
                <SvgIcon name="edit" />
            </button>
            <!-- Its own button rather than CloseButton.vue, matching Blade: this is a
                 card's corner action carrying the delete icon, so it follows the form
                 card's edit control. `plain` opts out of the site-wide button skin. -->
            <button
                type="button"
                class="basket_item_remove plain"
                :aria-label="removeLabel"
                @click="emit('remove')"
            >
                <SvgIcon name="delete" />
            </button>
        </div>

        <!-- Withheld on an unrecognised type, where Blade throws - the rest of the line
             still tells the customer what they are being charged for. -->
        <Chip v-if="typeLabel" :label="typeLabel" :icon="icon" />

        <div class="basket_item_figures">
            <!-- The meta parts carry no separators of their own - basket-item.scss draws
                 one between adjacent spans, so a missing date or a single guest can never
                 leave a stray middot behind. -->
            <p v-if="hasMeta" class="basket_item_meta">
                <span v-if="locationName">{{ locationName }}</span>
                <span v-if="dateLabel">{{ dateLabel }}</span>
                <span v-if="peopleLabel">{{ peopleLabel }}</span>
            </p>
            <!-- The voucher replaces the price rather than sitting beside it, as it does
                 in the checkout summary and V1's own basket partial: there is no price left
                 to state. A room premium or a delivery charge is still payable and still
                 shown in the totals below - this is the line's own figure only. -->
            <span v-if="voucherProvider" class="basket_item_voucher_note">
                Paid for by your {{ voucherProvider }} voucher
            </span>
            <!-- The struck figure sits inside the price element rather than beside it, so the
                 row's margin-left:auto and its no-wrap rule keep applying to the pair. The
                 hidden words are what stop a screen reader reading out two bare amounts:
                 strikethrough is not reliably announced. -->
            <span v-else class="basket_item_price">
                <s v-if="wasPriceString" class="basket_item_was_price">
                    <span class="visually_hidden">Was </span>{{ wasPriceString }}
                </s>
                <span v-if="wasPriceString" class="visually_hidden">now </span>{{ priceString }}
            </span>
        </div>

        <!-- Why there is no edit control on this row. Only for a held reservation: a gift
             item simply has no edit path and saying so would be noise on every gift line.
             The action that clears it lives at checkout, next to the held slot itself. -->
        <p v-if="locked" class="basket_item_note">
            We are holding this appointment for you. To change the spa, date or guests, cancel
            the reservation at checkout first.
        </p>
    </li>
</template>
